import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, effect, input, model, output, signal } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { DocumentiService } from 'src/app/core/documenti.service';
import { autocompleteFilter } from 'src/app/shared/helpers/autocompleteFilter.helper';
import { Classifica } from 'src/app/shared/models/classifica.model';
import { Declassifica } from 'src/app/shared/models/declassifica.model';
import { ClassificaI18nPipe } from 'src/app/shared/pipes/classifica-i18n.pipe';

@Component({
  selector: 'app-declassifica-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <p-dialog header="Eleva/Declassifica/Proroga" [(visible)]="visible" [modal]="true" (onHide)="hideDialog()" >
    <label for="classifica" class="small-label">Nuova classifica</label>
    <p-autoComplete id="classifica" [suggestions]="classificheFiltrate" [delay]="20" [(ngModel)]="nuovaClassifica" (ngModelChange)="checkIsProroga()"
      (completeMethod)="classificheFiltrate = autocompleteFilter($event, classifiche, 'nomeClassifica')" [dropdown]="true" [size]="20" appendTo="body"
      dataKey="classificaId" [forceSelection]="true" field="nomeClassifica" [autoHighlight]="true" [required]="true" [inputStyle]="{'width': '187px'}">
    </p-autoComplete>

    <label for="dal" class="small-label">Dal</label>
    <p-calendar inputId="dal"  [showIcon]="true" dateFormat="dd/mm/yy" [selectOtherMonths]="true" appendTo="body" [(ngModel)]="dateFrom" [inputStyle]="{'width': '187px'}" [disabled]="isProroga()"></p-calendar>

    <label for="al" class="small-label">Al</label>
    <p-calendar inputId="al" [showIcon]="true" dateFormat="dd/mm/yy" [selectOtherMonths]="true" appendTo="body" [(ngModel)]="dateTo" [inputStyle]="{'width': '187px'}"></p-calendar>

    <label for="note" class="small-label">Nota per registro</label>
    <textarea pInputTextarea id="note" rows="5" cols="30" [(ngModel)]="note"></textarea>
    
    <div class="vietata-checkbox">
      <p-checkbox name="vietatadivulgazione" class="vietata-checkbox" label="Vietata divulgazione" [(ngModel)]="vietataDivulgazione" [binary]="true"></p-checkbox>
    </div>
    
    <p-footer>
      <button pButton type="button" icon="pi pi-times" label="Annulla" (click)="hideDialog()" class="p-button-secondary"></button>
      <button pButton type="button" icon="pi pi-check" label="Salva" (click)="declassify_click()" [disabled]="!nuovaClassifica || (!dateFrom && !isProroga()) || (!dateTo && isProroga())"></button>
    </p-footer>
  </p-dialog>
  `,
  styles: [`
  label { margin-bottom: 4px; margin-top: 20px; color: inherit; }
  .vietata-checkbox { margin-bottom: 4px; margin-top: 20px; color: inherit; }
  label:first-of-type { margin-top: 0; }
  #numeronota, #note { min-height: 50px!important}
  `]
})

export class DeclassificaDialogComponent implements OnInit {
  visible = model.required<boolean>();
  documentId = input<number | undefined>();
  qualifica = input<number | null | undefined>();
  classifica = input<number | null | undefined>();

  hide = output();
  done = output<Declassifica>();
  fail = output<HttpErrorResponse>();

  public classifiche: Classifica[] = [];
  public classificheFiltrate: Classifica[] = [];
  public nuovaClassifica: Classifica | null = null;
  public dateFrom: Date | null = null;
  public dateTo: Date | null = null;
  public note = '';
  public vietataDivulgazione = false;
  public autocompleteFilter = autocompleteFilter;

  public isProroga = signal<boolean>(false);

  private _qualifica: number | null | undefined;
  private readonly classificai18n: ClassificaI18nPipe;

  constructor(
    private readonly appConfig: AppConfig,
    private readonly anagraficheService: AnagraficheService,
    private readonly confirmationService: ConfirmationService,
    private readonly documentiService: DocumentiService
  ) {
    effect(() => {
      this._qualifica = this.qualifica();
      this.refreshClassifiche();
    });
    this.classificai18n = new ClassificaI18nPipe(this.appConfig);
  }

  ngOnInit(): void {
    this.refreshClassifiche();
  }

  private refreshClassifiche(): void {
    this.anagraficheService.classifiche.getAllArray(c => {
      this.classifiche = c
        .map(e => {
          return {
            ...e,
            nomeClassifica: this.classificai18n.transform(e.nomeClassifica, this._qualifica)
          }
        })
        .filter(e => e.nomeClassifica !== null)
    });
  }

  checkIsProroga(): void {
    this.isProroga.set(this.nuovaClassifica?.classificaId === this.classifica());
  }

  hideDialog(): void {
    this.nuovaClassifica = null;
    this.dateFrom = null;
    this.dateTo = null;
    this.note = '';
    this.vietataDivulgazione = false;
    this.hide.emit();
  }

  declassify_click(): void {
    const documentId = this.documentId();

    if (this.nuovaClassifica === null || ( this.dateFrom === null && !this.isProroga()) || ( this.dateTo === null && this.isProroga()) || !documentId || documentId < 0) return;

    if ( this.dateFrom && this.dateTo && this.dateTo.getTime() < this.dateFrom.getTime() ) return;

    const request: Declassifica = {
      classification: this.nuovaClassifica,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      note: this.note,
      vietataDivulgazione: this.vietataDivulgazione
    };

    let message = `Applicare al documento la classifica <strong>${this.nuovaClassifica.nomeClassifica}</strong>?`;
    let acceptLabel= 'Conferma';

    if ( this.isProroga()) {
      message = `Prorogare la data di scadenza del documento fino al <strong>${this.dateTo?.toLocaleDateString()}</strong>?`;
      acceptLabel= 'Proroga';
    }

    this.confirmationService.confirm({
      message: message,
      acceptLabel: acceptLabel,
      accept: () => {
        this.documentiService.declassify(documentId, request).subscribe({
          next: () => {
            this.done.emit(request);
            this.hideDialog();
          },
          error: (error: HttpErrorResponse) => {
            this.fail.emit(error);
          }
        });
      }
    });
  }
}
